import React, { ReactElement, useEffect, useState } from 'react';
import { useRef } from 'react';
import { Debug } from '../../../../common/utils/debugLogs';
import { randomUUID } from '../../../../common/utils/utils';
import { ResponseAudience } from '../../../../services/api/models/response/responseAudience';
import { LiveProps } from '../../LiveContainer';
import { GiftMessage } from '../../models/giftMessage';
import { convertRtmMessage } from '../../models/liveComment';
import { MessageType } from '../../types/messageType';
import { AnimationPlayer } from './AnimationPlayer';
import { GiftCombo } from './GiftCombo';
import './LiveGift.scss';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../store/store';

export const LiveGift = (props: LiveProps): ReactElement => {
  // when multiple gift coming if last gift was not finish animation, add it to queue
  const giftQueue = useRef<Array<GiftMessage>>([]);
  const liveAuthId = props.liveState.liveData?.id;

  // map between loginId and combo timeout
  // use this to prevent create multiple timeout for one loginId
  // when combo count increase, destroy old timeout and update new
  const giftComboTimeMap = useRef(new Map());
  const giftCombo = useRef(new Map());

  const [isInit, setIsInit] = useState(false);
  const [, triggerRender] = useState('');
  const resizeStatus = useSelector(
    (state: AppState) => state.live.resizeStatus,
  );
  const [isVerticalScreen, setIsVerticalScreen] = useState(true);

  // update combo count
  const updateGiftCombo = (key: string | undefined, value: GiftMessage) => {
    if (key === undefined) {
      return;
    }

    // remove from combo list if combo count is zero
    if (value.combo?.count !== undefined && value.combo?.count <= 0) {
      const newMap = new Map(giftCombo.current);
      newMap.delete(key);
      giftCombo.current = newMap;
      triggerRender(randomUUID());
      return;
    }

    if (value.combo?.count !== undefined && value.combo?.count > 1) {
      giftCombo.current = new Map(giftCombo.current.set(key, value));
      triggerRender(randomUUID());
    }
  };

  // Callback when combo finished by 3s timeout
  // - Each loginId only have one timeout for combo
  const onComboFinish = (key: string) => {
    const currentTimeout = giftComboTimeMap.current.get(key);
    // clear old timeout
    if (currentTimeout !== undefined) {
      clearTimeout(currentTimeout);
    }

    // refresh the time
    const finishComboTimeout = setTimeout(() => {
      const newMap = new Map(giftCombo.current);
      newMap.delete(key);
      giftCombo.current = newMap;
      triggerRender(randomUUID());
    }, 3000);

    // update timeout map
    giftComboTimeMap.current = new Map(
      giftComboTimeMap.current.set(key, finishComboTimeout),
    );
  };

  const updateGiftTopAudience = (audienceList: ResponseAudience[]) => {
    if (props.liveState.liveData?.id === undefined) {
      return;
    }

    // update gift top
    if (audienceList.length > 0) {
      props.setGiftTopAudience(audienceList[0]);
    } else {
      props.setGiftTopAudience(undefined);
    }
  };

  // Callback when gift animation had finished
  const onAnimationFinish = () => {
    if (giftQueue.current.length > 0) {
      giftQueue.current = giftQueue.current.slice(1, giftQueue.current.length);
    }
    triggerRender(randomUUID());
  };

  useEffect(() => {
    if (isInit) {
      return;
    }

    if (
      !props.liveState.isInLive ||
      props.liveState.liveData === undefined ||
      props.liveState.userData === undefined
    ) {
      return;
    }

    props.liveState.rtmEngine?.on('ChannelMessage', (message: any): void => {
      const liveMessage = convertRtmMessage(JSON.stringify(message));
      if (liveAuthId !== liveMessage.liveId) {
        return;
      }
      if (
        liveMessage.commentConfig === MessageType.SEND_GIFT &&
        liveMessage.comment !== undefined
      ) {
        const sendGift = JSON.parse(liveMessage.comment) as GiftMessage;
        // default gift
        if (sendGift) {
          giftQueue.current = [...giftQueue.current, sendGift];
          triggerRender(randomUUID());
        }

        if (sendGift.combo !== undefined) {
          updateGiftCombo(
            `${sendGift.giftType?.animation?.uri}-${sendGift.sender?.loginId}`,
            sendGift,
          );
        }
      }

      if (
        liveMessage.commentConfig === MessageType.GIFT_TOP &&
        liveMessage.comment !== undefined
      ) {
        // get gift top audience info and update UI
        try {
          const audiences = JSON.parse(
            JSON.stringify(liveMessage.comment),
          ) as ResponseAudience[];
          updateGiftTopAudience(audiences);
        } catch (error: any) {
          Debug.log(error);
        }
      }
    });

    setIsInit(true);
  }, [props.liveState.isInLive]);

  useEffect(() => {
    const videoContainer = document.querySelector(
      '.live-media-player__content .live-media-player__video',
    ) as HTMLDivElement;
    if (!videoContainer) return;
    if (videoContainer.offsetWidth / videoContainer.offsetHeight > 0.8) {
      setIsVerticalScreen(false);
    } else {
      setIsVerticalScreen(true);
    }
  }, [resizeStatus]);

  return (
    <div
      className={`live-gift__container ${
        isVerticalScreen ? 'live-gift__container-mobile' : ''
      }`}
    >
      {/* Gift Message */}
      {giftQueue.current.length > 0 ? (
        <AnimationPlayer
          key={giftQueue.current[0].id}
          message={`${giftQueue.current[0].sender?.loginId} ${giftQueue.current[0].giftType?.name}`}
          messageId={giftQueue.current[0].id}
          url={giftQueue.current[0].giftType?.animation?.uri}
          onFinish={onAnimationFinish}
        />
      ) : (
        <></>
      )}

      {/* Gift combo */}
      <div className="gift-combo__list">
        {[...giftCombo.current.keys()].map((key: string, index: number) => {
          // maximum 2 combo at time
          if (index < 2) {
            const combo = giftCombo.current.get(key) as GiftMessage;
            return (
              <GiftCombo
                key={`${index}-${combo.giftType?.animation?.uri}-${combo.sender?.loginId}`}
                gift={combo}
                onFinish={() => onComboFinish(key)}
              ></GiftCombo>
            );
          }
          return <></>;
        })}
      </div>
    </div>
  );
};
